import { RoundedBox } from '@react-three/drei'
import type { PerfilCavalo } from '../../dominio/caminhaoTanque/perfil'

const CHASSI = '#242e38'
const VIDRO = '#203949'

type Vetor = [number, number, number]

function Peca({
  posicao,
  tamanho,
  cor,
  metal = 0,
  rugosidade = 0.45,
  raio = 0.03,
  rotacao,
  realce,
}: {
  posicao: Vetor
  tamanho: Vetor
  cor: string
  metal?: number
  rugosidade?: number
  raio?: number
  rotacao?: Vetor
  realce?: { emissive: string; emissiveIntensity: number }
}) {
  return (
    <RoundedBox
      position={posicao}
      args={tamanho}
      radius={raio}
      smoothness={2}
      rotation={rotacao}
      castShadow
      receiveShadow
    >
      <meshStandardMaterial
        color={cor}
        metalness={metal}
        roughness={rugosidade}
        {...realce}
      />
    </RoundedBox>
  )
}

export function Cabine3D({
  perfil,
  cor,
  realce,
}: {
  perfil: PerfilCavalo
  cor: string
  realce: { emissive: string; emissiveIntensity: number }
}) {
  const frente = perfil.zCentroM + perfil.comprimentoM / 2
  const meiaLargura = perfil.larguraM / 2
  return (
    <group>
      <Peca
        posicao={[0, 2.28, frente - 1.12]}
        tamanho={[perfil.larguraM, 2.24, 2.24]}
        cor={cor}
        metal={0.25}
        raio={0.14}
        realce={realce}
      />
      <Peca
        posicao={[0, 3.35, frente - 1.23]}
        tamanho={[perfil.larguraM * 0.97, 0.42, 2.05]}
        cor={cor}
        metal={0.25}
        raio={0.12}
      />
      <Peca
        posicao={[0, 2.71, frente + 0.025]}
        tamanho={[perfil.larguraM * 0.89, 0.86, 0.05]}
        cor="#101b24"
        raio={0.02}
      />
      <Peca
        posicao={[0, 2.73, frente + 0.058]}
        tamanho={[perfil.larguraM * 0.85, 0.73, 0.025]}
        cor={VIDRO}
        metal={0.45}
        rugosidade={0.12}
        raio={0.008}
      />
      <Peca
        posicao={[0, 3.03, frente + 0.075]}
        tamanho={[perfil.larguraM * 0.82, 0.025, 0.018]}
        cor="#72959f"
        metal={0.4}
        raio={0.005}
      />
      {[-1, 1].map((lado) => (
        <group key={lado}>
          <Peca
            posicao={[lado * 0.46, 2.4, frente + 0.088]}
            tamanho={[0.63, 0.018, 0.018]}
            cor="#111a21"
            rotacao={[0, 0, lado * 0.12]}
            raio={0.004}
          />
          <Peca
            posicao={[lado * meiaLargura, 2.7, frente - 1.1]}
            tamanho={[0.035, 0.91, 1.39]}
            cor="#152430"
            raio={0.012}
          />
          <Peca
            posicao={[lado * (meiaLargura + 0.021), 2.72, frente - 1.09]}
            tamanho={[0.02, 0.77, 1.22]}
            cor={VIDRO}
            metal={0.4}
            rugosidade={0.15}
            raio={0.006}
          />
          <Peca
            posicao={[lado * (meiaLargura + 0.036), 2.68, frente - 0.9]}
            tamanho={[0.018, 0.8, 0.04]}
            cor="#17242e"
            raio={0.005}
          />
          <Peca
            posicao={[lado * (meiaLargura + 0.018), 1.95, frente - 1.3]}
            tamanho={[0.022, 0.04, 1.26]}
            cor="#97a7ae"
            raio={0.008}
          />
          <Peca
            posicao={[lado * (meiaLargura + 0.043), 2.13, frente - 1.62]}
            tamanho={[0.04, 0.055, 0.23]}
            cor={CHASSI}
            raio={0.014}
          />
          <Peca
            posicao={[lado * (meiaLargura + 0.13), 2.67, frente - 0.18]}
            tamanho={[0.3, 0.04, 0.04]}
            cor={CHASSI}
            raio={0.012}
          />
          <Peca
            posicao={[lado * (meiaLargura + 0.27), 2.54, frente - 0.1]}
            tamanho={[0.14, 0.43, 0.22]}
            cor={CHASSI}
            raio={0.035}
          />
          <Peca
            posicao={[lado * (meiaLargura + 0.345), 2.54, frente - 0.1]}
            tamanho={[0.01, 0.34, 0.15]}
            cor="#8ba8b3"
            metal={0.85}
            rugosidade={0.1}
            raio={0.003}
          />
          {[0.66, 0.94].map((y) => (
            <Peca
              key={y}
              posicao={[lado * 1.09, y, frente - 1.94]}
              tamanho={[0.38, 0.12, 0.65]}
              cor="#758692"
              metal={0.7}
              rugosidade={0.45}
              raio={0.02}
            />
          ))}
          <Peca
            posicao={[lado * 0.88, 1.08, frente + 0.078]}
            tamanho={[0.43, 0.28, 0.11]}
            cor={CHASSI}
            raio={0.055}
          />
          <Peca
            posicao={[lado * 0.88, 1.12, frente + 0.14]}
            tamanho={[0.33, 0.12, 0.022]}
            cor="#eaf2eb"
            metal={0.15}
            rugosidade={0.16}
            raio={0.006}
          />
          <Peca
            posicao={[lado * 1.07, 0.92, frente + 0.14]}
            tamanho={[0.08, 0.05, 0.025]}
            cor="#e9a13f"
            raio={0.008}
          />
          <Peca
            posicao={[lado * 0.72, 3.57, frente - 0.47]}
            tamanho={[0.16, 0.04, 0.12]}
            cor="#f5d49d"
            raio={0.015}
          />
          <mesh
            position={[lado * (meiaLargura + 0.025), 0.52, frente - 1.25]}
            rotation={[0, Math.PI / 2, 0]}
          >
            <torusGeometry args={[0.63, 0.08, 8, 24, Math.PI]} />
            <meshStandardMaterial color={CHASSI} roughness={0.65} />
          </mesh>
        </group>
      ))}
      <Peca
        posicao={[0, 1.67, frente + 0.055]}
        tamanho={[perfil.larguraM * 0.7, 0.73, 0.09]}
        cor={CHASSI}
        raio={0.065}
      />
      {[1.43, 1.59, 1.75, 1.91].map((y) => (
        <Peca
          key={y}
          posicao={[0, y, frente + 0.11]}
          tamanho={[perfil.larguraM * 0.62, 0.055, 0.035]}
          cor="#536571"
          metal={0.65}
          raio={0.01}
        />
      ))}
      <Peca
        posicao={[0, 0.65, frente + 0.025]}
        tamanho={[perfil.larguraM, 0.3, 0.3]}
        cor={CHASSI}
        metal={0.2}
        raio={0.06}
      />
      <Peca
        posicao={[0, 0.45, frente + 0.065]}
        tamanho={[perfil.larguraM * 0.92, 0.08, 0.22]}
        cor="#74828c"
        metal={0.65}
        raio={0.025}
      />
      {[-0.42, 0.42].map((x) => (
        <mesh key={x} position={[x, 0.91, perfil.zCentroM - 0.4]} castShadow>
          <boxGeometry args={[0.16, 0.26, perfil.comprimentoM - 0.6]} />
          <meshStandardMaterial
            color={CHASSI}
            metalness={0.25}
            roughness={0.6}
          />
        </mesh>
      ))}
      {[frente - 3.1, frente - 4.5, frente - 5.5].map((z) => (
        <mesh key={z} position={[0, 0.91, z]}>
          <boxGeometry args={[1.1, 0.16, 0.15]} />
          <meshStandardMaterial color={CHASSI} />
        </mesh>
      ))}
      <mesh position={[0, 1.12, frente - 4.4]}>
        <cylinderGeometry args={[0.56, 0.56, 0.12, 24]} />
        <meshStandardMaterial
          color="#46505a"
          metalness={0.55}
          roughness={0.55}
        />
      </mesh>
      {[-1, 1].map((lado) => (
        <group key={lado}>
          <mesh
            position={[lado * 0.93, 0.85, frente - 3.05]}
            rotation={[Math.PI / 2, 0, 0]}
            castShadow
          >
            <cylinderGeometry args={[0.3, 0.3, 1.18, 24]} />
            <meshStandardMaterial
              color="#acb8c3"
              metalness={0.8}
              roughness={0.28}
            />
          </mesh>
          {[-0.4, 0.4].map((z) => (
            <mesh
              key={z}
              position={[lado * 0.93, 0.85, frente - 3.05 + z]}
              rotation={[Math.PI / 2, 0, 0]}
            >
              <torusGeometry args={[0.307, 0.028, 8, 24]} />
              <meshStandardMaterial
                color="#465561"
                metalness={0.5}
                roughness={0.4}
              />
            </mesh>
          ))}
          <Peca
            posicao={[lado * 0.95, 1.14, frente - 4.52]}
            tamanho={[0.57, 0.13, 2.27]}
            cor={CHASSI}
            raio={0.035}
          />
        </group>
      ))}
    </group>
  )
}
